import { ILoginUser, IInsertUser, IJoinGroup, IUpdatePurge } from "store/user-store";
import { IInsertJetton } from "store/jetton-list-store";
import axiosInstance from "./axiosInstance";
import qs from "qs";
import {
  BuyTonPreviewResponse,
  CabalDetails,
  CoinPriceRatio,
  Jetton,
  JettonBalance,
  JettonPrice,
  JettonPriceRatio,
  PaginatedData,
  SellTonPreviewResponse,
  Token,
  TokenCall,
  UserPortfolioAssets,
  UserPortfolioEarning,
  UserPortfolioJoinedGroup,
  TokenHolder,
  TokenPrice,
  UserBalance,
  UserPortfolioAssetsPaginatedData,
  User,
  Cabal,
} from "types";
import { Chain } from "chainConfigs";
const { REACT_APP_API_URL, REACT_APP_TON_CLIENT_API_URL } = process.env;

const isTestnet = process.env.REACT_APP_TESTNET === "1";

async function redeemCode(code: string) {
  return {
    res: (await axiosInstance.post(REACT_APP_API_URL + "/v1/utils/code_redeem", { code })).data,
  };
}

async function verifyToken(refreshToken: string) {
  return {
    res: (await axiosInstance.post(REACT_APP_API_URL + "/v1/auth/verify-tokens", { refreshToken }))
      .data,
  };
}

async function insertJetton(data: IInsertJetton) {
  const endpoint = REACT_APP_API_URL + "/v1/cabals/insert";
  const res = await axiosInstance.post(endpoint, data);

  return {
    res: res.data,
    status: res.status,
  };
}

// V2
async function insertJettonV2(data: {
  image: string;
  name: string;
  tgUserId: number;
  tgUserName: string;
  reserveRate: string;
  description: string;
  symbol: string;
  decimals: string;
  language: string;
}) {
  const endpoint = REACT_APP_API_URL + "/v1/cabals/insert";
  const res = await axiosInstance.post(
    endpoint,
    {
      ...data,
      chain: isTestnet ? "ton_testnet" : "ton",
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return {
    res: res.data,
    status: res.status,
  };
}

async function getJettonList(chain: string, field?: string) {
  let endpoint = `${REACT_APP_API_URL}/v1/cabals?limit=100&sortBy=createdAt:desc&chain=${chain}`;
  if (field) endpoint += `&field=${field}`;
  return {
    res: (await axiosInstance.get(endpoint)).data,
  };
}

async function getJetton(masterAddress: string) {
  const endpoint = REACT_APP_API_URL + "/v1/cabals/" + masterAddress;

  return {
    res: (await axiosInstance.get(endpoint)).data,
  };
}

async function getJettonPrice(masterAddress: string, limit: number) {
  const endpoint = REACT_APP_API_URL + `/v1/cabals/${masterAddress}/price?limit=${limit}`;

  return {
    res: (await axiosInstance.get(endpoint)).data,
  };
}

async function getJettonHolders(masterAddress: string, chain: string) {
  const endpoint = REACT_APP_API_URL + `/v1/cabals/${masterAddress}/holders?chain=${chain}`;

  return {
    res: (await axiosInstance.get(endpoint)).data,
  };
}

async function getJettonTxns(masterAddress: string, chain: string) {
  const endpoint = REACT_APP_API_URL + `/v1/cabals/${masterAddress}/txns?chain=${chain}`;

  return {
    res: (await axiosInstance.get(endpoint)).data,
  };
}

async function getJettonUpdates(
  masterAddress: string,
  jettonId: string,
  lt: number,
  chain: string,
) {
  const endpoint =
    REACT_APP_API_URL + `/v1/cabals/${masterAddress}/${jettonId}?lt=${lt}&chain=${chain}`;

  return {
    res: (await axiosInstance.get(endpoint)).data,
  };
}

async function getJettonsByOwner(ownerAddress: string, network: string) {
  const endpoint = REACT_APP_API_URL + `/v1/cabals/${ownerAddress}/profile/list?chain=${network}`;

  return {
    res: (await axiosInstance.get(endpoint)).data,
  };
}

async function getTonPrice() {
  const endpoint = REACT_APP_TON_CLIENT_API_URL + "/v2/rates?tokens=ton&currencies=usd";

  return (await axiosInstance.get(endpoint)).data;
}

async function loginUser(data: ILoginUser) {
  const endpoint = REACT_APP_API_URL + "/v1/auth/login";

  return {
    res: (await axiosInstance.post(endpoint, data)).data,
  };
}

async function createUser(data: IInsertUser) {
  const endpoint = REACT_APP_API_URL + "/v1/auth/register";

  return {
    res: (await axiosInstance.post(endpoint, data)).data,
  };
}

async function joinGroup(data: IJoinGroup) {
  const endpoint = REACT_APP_API_URL + "/v1/cabals/joinGroup";

  return {
    res: (await axiosInstance.post(endpoint, data)).data,
  };
}

async function updateJettonPurge(data: IUpdatePurge) {
  const endpoint = REACT_APP_API_URL + "/v1/cabals/updatePurge";

  return {
    res: (await axiosInstance.post(endpoint, data)).data,
  };
}

async function uploadFile(file: File): Promise<string | undefined> {
  const endpoint = REACT_APP_API_URL + "/v1/utils/upload";

  const formData = new FormData();
  formData.append("file", file);

  try {
    return (
      await axiosInstance.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
    ).data.url;
  } catch (error) {
    console.error("Error uploading file:", error);
  }
}

async function getGroupChatMsgs(groupId: string) {
  const endpoint = REACT_APP_API_URL + "/v1/cabals/groupChat?tgGroupId=" + groupId;

  try {
    return (await axiosInstance.get(endpoint)).data;
  } catch (error) {
    console.error("Error getGroupChatMsgs:", error);
  }
}

async function getTokenBalance({
  tokenAddress,
  walletAddress,
  chain,
  type = "archive",
}: {
  tokenAddress: string;
  walletAddress: string;
  chain: string;
  type?: "realTime" | "archive";
}) {
  let result;
  try {
    const endpoint =
      REACT_APP_API_URL +
      `/v1/cabalCalls/getCabalCalls/${tokenAddress}/balance?walletAddress=${walletAddress}&chain=${chain}&type=${type}`;

    result = (
      await axiosInstance.get(endpoint, {
        headers: {
          "Content-Type": "application/json",
        },
      })
    ).data;
  } catch (error) {
    if (result?.meesage) {
      console.error("Error getTokenBalance:", result?.meesage);
    } else {
      console.error("Error getTokenBalance:", error);
    }
  }
  return result ?? { message: "Error" };
}

async function tokenTradePreview(
  {
    srcToken,
    destToken,
    senderAddress,
    nativeSpent,
    slippage,
    srcChain,
    destChain,
    tradeType,
    tgGroupId,
    isSellAll,
    isBuyAll,
    destinationAddress,
    dstChainOrderAuthorityAddress,
  }: {
    srcToken: string;
    destToken: string;
    senderAddress: string;
    nativeSpent: number;
    slippage: number;
    srcChain: string;
    destChain: string;
    tradeType: string;
    tgGroupId?: number;
    isSellAll?: boolean;
    isBuyAll?: boolean;
    destinationAddress?: string;
    dstChainOrderAuthorityAddress?: string;
  },
  signal: AbortSignal,
) {
  let result;
  try {
    const endpoint = REACT_APP_API_URL + `/v1/trade/tokenSwapPreview`;

    result = (
      await axiosInstance.post(
        endpoint,
        {
          srcToken,
          destToken,
          senderAddress,
          nativeSpent,
          slippage,
          srcChain,
          destChain,
          tradeType,
          ...(srcChain !== destChain ? { destinationAddress, dstChainOrderAuthorityAddress } : {}),
          ...(tgGroupId ? { tgGroupId } : {}),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          signal,
        },
      )
    ).data;

    return result;
  } catch (error) {
    if (result?.meesage) {
      console.error("Error tokenTradePreview:", result.meesage);
    } else {
      console.error("Error tokenTradePreview:", error);
    }

    return {
      data: result ?? { message: "Error" },
    };
  }
}

async function getNativePrice({ tokenAddress, chain }: { tokenAddress: string; chain: string }) {
  let result;
  try {
    const endpoint =
      REACT_APP_API_URL + `/v1/trade/nativePrice?tokenAddress=${tokenAddress}&chain=${chain}`;

    result = (
      await axiosInstance.get(endpoint, {
        headers: {
          "Content-Type": "application/json",
        },
      })
    ).data;
    return result;
  } catch (error) {
    if (result?.meesage) {
      console.error("Error getNativePrice:", error);
    } else {
      console.error("Error getNativePrice:", error);
    }
    return result;
  }
}

async function tokenTrade({
  srcToken,
  destToken,
  senderAddress,
  nativeSpent,
  priceUsd,
  slippage,
  chain,
  tradeType,
  tgGroupId,
  isSellAll,
  isBuyAll,
}: {
  srcToken: string;
  destToken: string;
  senderAddress: string;
  nativeSpent: number;
  priceUsd: number;
  slippage: number;
  chain: string;
  tradeType: string;
  tgGroupId: number;
  isSellAll: boolean;
  isBuyAll: boolean;
}) {
  try {
    const endpoint = REACT_APP_API_URL + `/v1/trade/tokenSwap`;

    return (
      await axiosInstance.post(
        endpoint,
        {
          srcToken,
          destToken,
          senderAddress,
          nativeSpent,
          priceUsd,
          slippage,
          chain,
          tradeType,
          isSellAll,
          isBuyAll,
          ...(tgGroupId ? { tgGroupId } : {}),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
    ).data;
  } catch (error) {
    console.error("Error getTokenBalance:", error);
  }
}

async function crossChainTrade({
  srcToken,
  destToken,
  senderAddress,
  nativeSpent,
  slippage,
  srcChain,
  destChain,
  tradeType,
  isSellAll,
  isBuyAll,
  priceUsd,
  tgGroupId,
  isFromTrendingCall,
  destinationAddress,
  dstChainOrderAuthorityAddress,
  isSwap,
  tokenPrice,
}: {
  srcToken: string;
  destToken: string;
  senderAddress: string;
  nativeSpent: number;
  slippage: number;
  srcChain: string;
  destChain: string;
  tradeType: string;
  isSellAll: boolean;
  isBuyAll: boolean;
  priceUsd: number;
  tgGroupId?: number;
  isFromTrendingCall?: boolean;
  destinationAddress?: string;
  dstChainOrderAuthorityAddress?: string;
  isSwap?: boolean;
  tokenPrice?: number;
}) {
  try {
    const chain = isTestnet ? "ton_testnet" : "ton";
    const endpoint = REACT_APP_API_URL + `/v1/trade/tokenSwap?chain=${chain}`;

    return (
      await axiosInstance.post(
        endpoint,
        {
          srcToken,
          destToken,
          senderAddress,
          nativeSpent,
          slippage,
          srcChain,
          destChain,
          tradeType,
          isSellAll,
          isBuyAll,
          priceUsd,
          ...(isFromTrendingCall ? { isFromTrendingCall } : {}),
          ...(srcChain !== destChain ? { destinationAddress, dstChainOrderAuthorityAddress } : {}),
          ...(tgGroupId ? { tgGroupId } : {}),
          ...(isSwap ? { isSwap } : {}),
          ...(tokenPrice ? { tokenPrice } : {}),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
    ).data;
  } catch (error) {
    console.error("Error getTokenBalance:", error);
  }
}

async function crossChainTradeCustodial({
  srcToken,
  destToken,
  senderAddress,
  nativeSpent,
  slippage,
  srcChain,
  destChain,
  tradeType,
  isSellAll,
  isBuyAll,
  priceUsd,
  tgGroupId,
  isFromTrendingCall,
  destinationAddress,
  dstChainOrderAuthorityAddress,
  isSwap,
  tokenPrice,
}: {
  srcToken: string;
  destToken: string;
  senderAddress: string;
  nativeSpent: number;
  slippage: number;
  srcChain: string;
  destChain: string;
  tradeType: string;
  isSellAll: boolean;
  isBuyAll: boolean;
  priceUsd: number;
  tgGroupId?: number;
  isFromTrendingCall?: boolean;
  destinationAddress?: string;
  dstChainOrderAuthorityAddress?: string;
  isSwap?: boolean;
  tokenPrice?: number;
}) {
  try {
    const endpoint = REACT_APP_API_URL + `/v1/trade/custodialTokenSwap`;

    return (
      await axiosInstance.post(
        endpoint,
        {
          srcToken,
          destToken,
          senderAddress,
          nativeSpent,
          slippage,
          srcChain,
          destChain,
          tradeType,
          isSellAll,
          isBuyAll,
          priceUsd,
          ...(isFromTrendingCall ? { isFromTrendingCall } : {}),
          ...(srcChain !== destChain ? { destinationAddress, dstChainOrderAuthorityAddress } : {}),
          ...(tgGroupId ? { tgGroupId } : {}),
          ...(isSwap ? { isSwap } : {}),
          ...(tokenPrice ? { tokenPrice } : {}),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
    ).data;
  } catch (error) {
    console.error("Error getTokenBalance:", error);
  }
}

async function updateTrade({
  tradeId,
  hash,
  bundleId,
  signedTxn,
  isTransfer,
}: {
  tradeId: string;
  hash?: string;
  bundleId?: string;
  signedTxn?: string[];
  isTransfer?: boolean;
}) {
  const endpoint = REACT_APP_API_URL + `/v1/trade/updateTrade`;
  return (await axiosInstance.patch(endpoint, { tradeId, hash, bundleId, signedTxn, isTransfer }))
    .data;
}

async function getCabalCalls({
  limit = 10,
  page = 1,
  sortBy = "createdAt:desc",
  selects,
  field,
  filter,
  selectedDay,
}: {
  sortBy?: string;
  limit?: number;
  page?: number;
  selects?: string[];
  field?: string;
  filter?: Record<string, string | undefined>;
  selectedDay?: "day7Result" | "day14Result" | "day1Result";
}) {
  const queryString = qs.stringify({
    selectedDay,
    field,
    filter: Object.entries(filter ?? {})
      .filter(([k, v]) => v !== undefined)
      .filter(([k, v]) => k !== "language" || v !== "all")
      .map(([k, v]) => `${k}:${v}`)
      .join(","),
    limit,
    page,
    sortBy,
    selects: selects?.join(" "),
  });
  const endpoint = REACT_APP_API_URL + `/v1/cabalCalls/getCabalCalls?${queryString}`;
  return (await axiosInstance.get<PaginatedData<TokenCall>>(endpoint)).data;
}
async function getJoinedCabalCalls({
  limit = 10,
  page = 1,
  sortBy = "createdAt:desc",
  selects,
  field,
  filter,
  selectedDay,
}: {
  sortBy?: string;
  limit?: number;
  page?: number;
  selects?: string[];
  field?: string;
  filter?: Record<string, string | undefined>;
  selectedDay?: "day7Result" | "day14Result" | "day1Result";
}) {
  const queryString = qs.stringify({
    selectedDay,
    field,
    chain: isTestnet ? "ton_testnet" : "ton",
    filter: Object.entries(filter ?? {})
      .filter(([k, v]) => v !== undefined)
      .filter(([k, v]) => k !== "language" || v !== "all")
      .map(([k, v]) => `${k}:${v}`)
      .join(","),
    limit,
    page,
    sortBy,
    selects: selects?.join(" "),
  });
  const endpoint = REACT_APP_API_URL + `/v1/cabalCalls/getJoinedCabalCalls?${queryString}`;
  return (await axiosInstance.get<PaginatedData<TokenCall>>(endpoint)).data;
}

async function getCabalDetails(id: string) {
  const endpoint = REACT_APP_API_URL + `/v1/cabals/groupId/${id}`;
  return (await axiosInstance.get<CabalDetails>(endpoint)).data;
}

async function getJettons({
  limit = 10,
  page = 1,
  sortBy,
  filter,
  field,
  selects,
  selectedDay = "all",
  name,
  tonAddress,
  tgUserId,
  language,
  groupType,
}: {
  limit?: number;
  page?: number;
  sortBy?: string;
  field?: string;
  filter?: Record<string, string | undefined>;
  selects?: string[];
  selectedDay?: "day7Result" | "day14Result" | "day1Result" | "all";
  name?: string;
  tonAddress?: string;
  tgUserId?: number;
  language?: string;
  groupType: string;
}) {
  const queryObject: Record<string, string | number | undefined> = {
    selectedDay,
    name,
    tgUserId,
    field,
    selects: selects?.join(" "),
    limit,
    page,
    sortBy,
    chain: isTestnet ? "ton_testnet" : "ton",
    filter: Object.entries(filter ?? {})
      .filter(([k, v]) => v !== undefined)
      .filter(([k, v]) => k !== "language" || v !== "all")
      .map(([k, v]) => `${k}:${v}`)
      .join(","),
  };

  if (tonAddress) {
    queryObject.tonAddress = tonAddress;
  }

  if (language && language !== "all") {
    queryObject.language = language;
  }

  if (groupType) {
    queryObject.groupType = groupType;
  }

  const queryString = qs.stringify(queryObject);
  const endpoint = REACT_APP_API_URL + `/v1/cabals?${queryString}`;
  return (await axiosInstance.get<PaginatedData<Jetton>>(endpoint)).data;
}

async function getJettonPriceV1({
  masterAddress,
  interval = "minute",
  sortBy = "createdAt:desc",
  page = 1,
  limit = 10000,
  selects = ["price", "updatedAt"],
}: {
  masterAddress: string;
  interval: "minute" | "hour" | "day" | "week";
  sortBy?: string;
  page?: number;
  limit?: number;
  selects?: string[];
}) {
  const queryString = qs.stringify({
    interval,
    sortBy,
    page,
    limit,
    selects: selects.join(" "),
  });

  const endpoint = REACT_APP_API_URL + `/v1/cabals/${masterAddress}/price?${queryString}`;
  return (await axiosInstance.get<PaginatedData<JettonPrice>>(endpoint)).data;
}

async function getJettonBalance({
  masterAddress,
  walletAddress,
  chain = isTestnet ? "ton_testnet" : "ton",
}: {
  masterAddress: string;
  walletAddress: string;
  chain?: string;
}) {
  const endpoint =
    REACT_APP_API_URL +
    `/v1/cabals/${masterAddress}/balance?chain=${chain}&walletAddress=${walletAddress}`;
  return (await axiosInstance.get<JettonBalance>(endpoint)).data;
}

async function buyTonPreview({
  masterAddress,
  priceUsd,
  chain = isTestnet ? "ton_testnet" : "ton",
}: {
  masterAddress: string;
  priceUsd: number;
  chain?: string;
}) {
  const endpoint = REACT_APP_API_URL + `/v1/trade/buyTonPreview`;
  return (
    await axiosInstance.post<BuyTonPreviewResponse>(endpoint, {
      masterAddress,
      priceUsd,
      chain,
    })
  ).data;
}

async function sellTonPreview({
  masterAddress,
  sellAmount,
  chain = isTestnet ? "ton_testnet" : "ton",
}: {
  masterAddress: string;
  sellAmount: number;
  chain?: string;
}) {
  const endpoint = REACT_APP_API_URL + `/v1/trade/sellTonPreview`;
  return (
    await axiosInstance.post<SellTonPreviewResponse>(endpoint, {
      masterAddress,
      sellAmount,
      chain,
    })
  ).data;
}

async function buyTonJetton({
  masterAddress,
  tonAmount,
  tgGroupId,
  tradeType,
  walletAddress,
  boc,
  fee,
}: {
  masterAddress: string;
  tonAmount: number;
  tgGroupId: number;
  tradeType: string;
  walletAddress: string;
  boc: any;
  fee: number;
}) {
  const endpoint = REACT_APP_API_URL + `/v1/trade/buyTonJetton`;
  return (
    await axiosInstance.post<{ status: number; message: string }>(endpoint, {
      masterAddress,
      tonAmt: tonAmount,
      tgGroupId,
      tradeType,
      walletAddress,
      boc,
      fee,
    })
  ).data;
}

async function getTonTxnMessage({
  publicKey,
  masterAddress,
  value,
}: {
  publicKey: string;
  masterAddress?: string;
  value?: number;
}) {
  const endpoint = REACT_APP_API_URL + `/v1/trade/getTonTxnMessage`;
  return (
    await axiosInstance.post<{ status: number; message: string }>(endpoint, {
      publicKey,
      masterAddress,
      value,
    })
  ).data;
}

async function getTxnMessage({
  amount,
  chain,
  tokenAddress,
  receiverAddress,
  isMax,
  isTransfer,
}: {
  amount: number;
  chain: string;
  tokenAddress?: string;
  receiverAddress: string;
  isMax?: boolean;
  isTransfer?: boolean;
}) {
  const endpoint = REACT_APP_API_URL + `/v1/trade/getTxnMessage`;
  return (
    await axiosInstance.post<{ status: number; message: string }>(endpoint, {
      amount,
      chain,
      tokenAddress,
      receiverAddress,
      isMax,
      isTransfer,
    })
  ).data;
}

async function buyJettonFromBridge({
  masterAddress,
  tonAmt,
  tgGroupId,
  chain,
}: {
  masterAddress: string;
  tonAmt: number;
  tgGroupId: number;
  chain?: string;
}) {
  const endpoint = REACT_APP_API_URL + `/v1/trade/buyJettonFromBridge`;

  return (
    await axiosInstance.post<{
      jobId: string;
      jobType: string;
      jobStatus: string;
      message: string;
      txnHash: String;
      status: number;
    }>(endpoint, {
      masterAddress,
      tonAmt,
      chain: chain === "ton" ? (isTestnet ? "ton_testnet" : "ton") : chain,
      tgGroupId,
    })
  ).data;
}

async function sellTonJetton({
  masterAddress,
  jettonAmt,
  tgGroupId,
  tradeType,
  walletAddress,
  boc,
  fee,
}: {
  masterAddress: string;
  jettonAmt: number;
  tgGroupId: number;
  tradeType: string;
  walletAddress: string;
  boc: any;
  fee: number;
}) {
  const endpoint = REACT_APP_API_URL + `/v1/trade/sellTonJetton`;
  return (
    await axiosInstance.post<{ status: number; message: string }>(endpoint, {
      masterAddress,
      jettonAmt,
      tgGroupId,
      tradeType,
      walletAddress,
      boc,
      fee,
    })
  ).data;
}

async function getToken({ tokenAddress, chain }: { tokenAddress: string; chain: Chain }) {
  const endpoint =
    REACT_APP_API_URL +
    `/v1/cabalCalls/getCabalCalls/${tokenAddress}${chain ? `?chain=${chain}` : ""}`;
  return (await axiosInstance.get<Token>(endpoint)).data;
}

async function getTokenHolders({
  tokenAddress,
  chain,
  signal,
}: {
  tokenAddress: string;
  chain: string;
  signal?: AbortSignal;
}) {
  const endpoint =
    REACT_APP_API_URL + `/v1/cabalCalls/getCabalCalls/${tokenAddress}/holders?chain=${chain}`;
  return (await axiosInstance.get<string>(endpoint, { signal })).data;
}

async function getCabalCallsPrice({
  pairAddress,
  chain,
  interval = "minute",
}: {
  pairAddress: string;
  chain: string;
  interval: "minute" | "hour" | "day" | "week";
}) {
  const queryString = qs.stringify({
    interval,
  });

  const endpoint =
    REACT_APP_API_URL +
    `/v1/cabalCalls/getCabalCalls/${pairAddress}/price?chain=${chain}&${queryString}`;
  return (await axiosInstance.get<TokenPrice[]>(endpoint)).data;
}

async function traceEvent({
  masterAddress,
  walletAddress,
}: {
  masterAddress: string;
  walletAddress: string;
}) {
  const endpoint = REACT_APP_API_URL + `/v1/trade/traceEvent`;
  const queryString = qs.stringify({
    masterAddress,
    walletAddress,
    chain: isTestnet ? "ton_testnet" : "ton",
  });
  return (
    await axiosInstance.get<{ status: number; message: string }>(`${endpoint}?${queryString}`)
  ).data;
}

async function joinGroupV1(data: {
  tokenAddress: string;
  walletAddress: string;
  tgUserName: string;
  isTrading: boolean;
}) {
  const chain = isTestnet ? "ton_testnet" : "ton";
  const endpoint = REACT_APP_API_URL + `/v1/cabals/joinGroup?chain=${chain}`;

  return (
    await axiosInstance.post<{ status: number; message: string; link?: string }>(endpoint, data)
  ).data;
}

async function getUserPortfolioAssets({ limit = 10, page = 1 }) {
  const url = new URL(`${REACT_APP_API_URL!}/v1/portfolio/assets`);
  url.searchParams.append("sortBy", "createdAt:desc");
  url.searchParams.append("limit", String(limit));
  url.searchParams.append("page", String(page));
  return (
    await axiosInstance.get<UserPortfolioAssetsPaginatedData<UserPortfolioAssets>>(
      url.toString(),
      {},
    )
  ).data;
}

async function getUserPortfolioGroups({ limit = 10, page = 1 }) {
  const url = new URL(`${REACT_APP_API_URL!}/v1/portfolio/joinedGroups`);
  url.searchParams.append("sortBy", "createdAt:desc");
  url.searchParams.append("limit", String(limit));
  url.searchParams.append("page", String(page));
  url.searchParams.append("chain", String(isTestnet ? "ton_testnet" : "ton"));

  return (await axiosInstance.get<PaginatedData<UserPortfolioJoinedGroup>>(url.toString(), {}))
    .data;
}

async function getUserPortfolioEarning({ limit = 10, page = 1 }) {
  const url = new URL(`${REACT_APP_API_URL!}/v1/portfolio/earnings`);
  url.searchParams.append("sortBy", "createdAt:desc");
  url.searchParams.append("limit", String(limit));
  url.searchParams.append("page", String(page));

  return (await axiosInstance.get<PaginatedData<UserPortfolioEarning>>(url.toString(), {})).data;
}

async function getUserBalance() {
  return (await axiosInstance.get<UserBalance>(`${REACT_APP_API_URL!}/v1/user/balance`, {})).data;
}

async function getUser(tgUserId: string) {
  return (await axiosInstance.get<User>(`${REACT_APP_API_URL!}/v1/user/${tgUserId}`, {})).data;
}

async function updateUser(tgUserId: number, data: any) {
  return (await axiosInstance.patch<User>(`${REACT_APP_API_URL!}/v1/user/${tgUserId}`, data)).data;
}

async function approveAllowance({
  chain,
  srcToken,
  destToken,
  senderAddress,
  amount,
}: {
  chain: string;
  srcToken: string;
  destToken: string;
  senderAddress: string;
  amount: number;
}) {
  return (
    await axiosInstance.post(
      `${REACT_APP_API_URL!}/v1/trade/allowanceApproval`,
      {
        chain,
        srcToken,
        destToken,
        senderAddress,
        amount,
      },
      {},
    )
  ).data;
}

async function getPrivateKey({ chain }: { chain?: Chain }) {
  let endpoint;
  endpoint = REACT_APP_API_URL + `/v1/wallet/exportWallet?chain=${chain}`;

  return (await axiosInstance.get(endpoint, {})).data;
}

async function getCurrentAllowance({
  chain,
  srcToken,
  destToken,
  senderAddress,
}: {
  chain: string;
  srcToken: string;
  destToken: string;
  senderAddress: string;
}) {
  return (
    await axiosInstance.post(
      `${REACT_APP_API_URL!}/v1/trade/allowance`,
      {
        chain,
        srcToken,
        destToken,
        senderAddress,
      },
      {},
    )
  ).data;
}

async function createWallet({
  email,
  challenge,
  attestation,
  subOrgId,
}: {
  email?: string;
  challenge?: string;
  attestation?: string;
  subOrgId?: string;
}) {
  return (
    await axiosInstance.post(`${REACT_APP_API_URL}/v1/user/createWallet`, {
      email,
      challenge,
      attestation,
      subOrgId,
    })
  ).data;
}

async function claimReward({
  chain,
  feeToken,
  totalFeeAmounts,
}: {
  chain: string;
  feeToken: string;
  totalFeeAmounts: number;
}) {
  try {
    const endpoint = `${REACT_APP_API_URL}/v1/portfolio/claim`;
    return (
      await axiosInstance.post(
        endpoint,
        {
          chain,
          feeToken,
          totalFeeAmounts,
        },
        {},
      )
    ).data;
  } catch (error) {
    console.error("Error claimReward:", error);
  }
}

async function createSolTokenAccount({
  toAddress,
  feeToken,
}: {
  toAddress: string;
  feeToken: string;
}) {
  const endpoint = `${REACT_APP_API_URL}/v1/portfolio/createSolTokenAcc`;
  return (
    await axiosInstance.post(
      endpoint,
      {
        toAddress,
        feeToken,
      },
      {},
    )
  ).data;
}

async function txnUpdate({
  srcToken,
  destToken,
  senderAddress,
  priceUsd,
  srcChain,
  destChain,
  tradeType,
  tgGroupId,
  hash,
  amount,
  feeToken,
  quantity,
  feeAmount,
  feeChain,
  isFromTrendingCall,
}: {
  srcToken: string;
  destToken: string;
  senderAddress: string;
  priceUsd: number;
  srcChain: string;
  destChain: string;
  tradeType: string;
  tgGroupId?: number;
  hash: string;
  amount: number;
  feeToken: string;
  quantity: number;
  feeAmount: number;
  feeChain: string;
  isFromTrendingCall?: boolean;
}) {
  const endpoint = `${REACT_APP_API_URL}/v1/trade/txnUpdate`;
  return (
    await axiosInstance.post(
      endpoint,
      {
        srcToken,
        destToken,
        senderAddress,
        priceUsd,
        srcChain,
        destChain,
        tradeType,
        hash,
        amount,
        feeToken,
        quantity,
        feeAmount,
        feeChain,
        ...(isFromTrendingCall ? { isFromTrendingCall } : {}),
        ...(tgGroupId ? { tgGroupId } : {}),
      },
      {},
    )
  ).data;
}

const getJoinedCabal = async ({
  tgUserId,
  tonAddress,
  language,
}: {
  tgUserId: number;
  tonAddress: string;
  language: string;
}) => {
  const url = new URL(`${REACT_APP_API_URL!}/v1/cabals/joinedCabals`);
  if (language !== "all") {
    url.searchParams.append("language", language);
  }
  url.searchParams.append("tgUserId", tgUserId.toString());
  url.searchParams.append("tonAddress", tonAddress);
  url.searchParams.append("chain", isTestnet ? "ton_testnet" : "ton");

  return (await axiosInstance.get(url.toString())).data;
};

const traceJobStatus = async (jobId: string, jobType: string) => {
  try {
    const url = new URL(`${REACT_APP_API_URL}/v1/trade/traceJobStatus`);
    url.searchParams.append("jobId", jobId);
    url.searchParams.append("jobType", jobType);

    return (await axiosInstance.get(url.toString())).data;
  } catch (error) {
    console.error("Error traceJobStatus:", error);
  }
};

const unwrapEth = async ({
  amount,
  senderAddress,
  tokenAddress,
  chain,
}: {
  amount: number;
  senderAddress: string;
  tokenAddress: string;
  chain: string;
}) => {
  try {
    const endpoint = `${REACT_APP_API_URL}/v1/portfolio/unwrapEth`;

    return (
      await axiosInstance.post(
        endpoint,
        {
          amount,
          senderAddress,
          tokenAddress,
          chain,
        },
        {},
      )
    ).data;
  } catch (error) {
    console.error("Error unwrapEth:", error);
  }
};

const unwrapSol = async ({
  senderAddress,
  tokenAddress,
}: {
  senderAddress: string;
  tokenAddress: string;
}) => {
  try {
    const endpoint = `${REACT_APP_API_URL}/v1/portfolio/unwrapSol`;

    return (
      await axiosInstance.post(
        endpoint,
        {
          senderAddress,
          tokenAddress,
        },
        {},
      )
    ).data;
  } catch (error) {
    console.error("Error unwrapEth:", error);
  }
};

const getTradeAllTrendingTokens = async ({
  sortBy,
  limit,
  page,
  chain,
}: {
  sortBy: string;
  limit: number;
  page: number;
  chain: string;
}) => {
  try {
    const url = new URL(`${REACT_APP_API_URL!}/v1/trade/allTrendingTokens`);

    url.searchParams.append("sortBy", sortBy);
    url.searchParams.append("limit", String(limit));
    url.searchParams.append("page", String(page));
    if (chain !== "all") {
      url.searchParams.append("chain", chain);
    }

    const result = (await axiosInstance.get(url.toString())).data;

    if (result && result.totalResults > 0) {
      return result;
    } else {
      return {
        results: [],
        totalResults: 0,
        totalPages: 1,
        page: 1,
        limit: limit,
        hasNext: false,
      };
    }
  } catch (error) {
    console.error("Failed to Get All Trade Tokens", error);
    return {
      results: [],
      totalResults: 0,
      totalPages: 1,
      page: 1,
      limit: limit,
      hasNext: false,
    };
  }
};

const getTradeJoinedGroupTokens = async ({
  sortBy,
  limit,
  page,
  chain,
}: {
  sortBy: string;
  limit: number;
  page: number;
  chain: string;
}) => {
  try {
    const url = new URL(`${REACT_APP_API_URL!}/v1/trade/groupTrendingTokens`);

    url.searchParams.append("sortBy", sortBy);
    url.searchParams.append("limit", String(limit));
    url.searchParams.append("page", String(page));
    if (chain !== "all") {
      url.searchParams.append("chain", chain);
    }

    const result = (await axiosInstance.get(url.toString())).data;

    if (result && result.totalResults > 0) {
      return result;
    } else {
      return {
        results: [],
        totalResults: 0,
        totalPages: 1,
        page: 1,
        limit: limit,
        hasNext: false,
      };
    }
  } catch (error) {
    console.error("Failed to Get Trade Joined Group Tokens", error);
    return {
      results: [],
      totalResults: 0,
      totalPages: 1,
      page: 1,
      limit: limit,
      hasNext: false,
    };
  }
};

const getTradeToken = async ({ query }: { query: string }) => {
  try {
    const url = new URL(`${REACT_APP_API_URL!}/v1/trade/getToken`);

    url.searchParams.append("query", query);

    return (await axiosInstance.get(url.toString())).data;
  } catch (error) {
    console.error("Failed to get trade token:", error);
  }
};

const sendToken = async ({
  receiverAddress,
  amount,
  chain,
  boc,
  hash,
  signedTxn,
  tokenAddress,
  isMax,
}: {
  receiverAddress: string;
  amount: number;
  chain: string;
  boc?: string;
  hash?: string;
  signedTxn?: string[];
  tokenAddress?: string;
  isMax?: boolean;
}) => {
  try {
    return (
      await axiosInstance.post(`${REACT_APP_API_URL!}/v1/portfolio/sendTokens`, {
        receiverAddress,
        amount,
        chain,
        boc,
        hash,
        signedTxn,
        tokenAddress,
        ...(isMax ? { isMax } : {}),
      })
    ).data;
  } catch (error) {
    console.error("Failed to send meme token:", error);
  }
};

const sendNativeToken = async ({
  receiverAddress,
  amount,
  chain,
  boc,
  hash,
  bundleId,
  signedTxn,
}: {
  receiverAddress?: string;
  amount?: number;
  chain?: string;
  boc?: string;
  hash?: string;
  bundleId?: string;
  signedTxn?: string[];
}) => {
  try {
    return (
      await axiosInstance.post(`${REACT_APP_API_URL!}/v1/portfolio/sendNativeTokens`, {
        receiverAddress,
        amount,
        chain,
        boc,
        hash,
        bundleId,
        signedTxn,
      })
    ).data;
  } catch (error) {
    console.error("Failed to send native token:", error);
  }
};

const sendEmailAuth = async ({
  email,
  targetPublicKey,
  organizationId,
  otpId,
  otpCode,
}: {
  email: string;
  targetPublicKey: string;
  organizationId?: string;
  otpId?: string;
  otpCode?: string;
}) => {
  try {
    return (
      await axiosInstance.post(`${process.env.REACT_APP_API_URL}/v1/user/emailAuth`, {
        email,
        targetPublicKey,
        organizationId,
        otpId,
        otpCode,
      })
    ).data;
  } catch (error) {
    console.error("Failed to send email auth:", error);
  }
};

const sendLogs = async ({ message }: { message: string }) => {
  try {
    await axiosInstance.post(`${process.env.REACT_APP_API_URL}/v1/utils/sendLogs`, { message });
  } catch (error) {
    console.error("Failed to send logs:", error);
  }
};

const getAllCabals = async ({ isActive }: { isActive?: boolean }): Promise<Cabal[]> => {
  try {
    return (
      await axiosInstance.get(
        `${REACT_APP_API_URL!}/v1/cabals/getFreeModeCabals${
          isActive !== undefined ? `?isActive=${isActive.toString()}` : ""
        }`,
      )
    ).data;
  } catch (error) {
    console.error("Failed to get all inactive cabals:", error);
    return [];
  }
};

interface activateCabalPayload {
  name: string;
  imageUri: string;
  language: string;
}

const activateCabalByCabalId = async ({
  body,
  tgGroupId,
}: {
  body: activateCabalPayload;
  tgGroupId: number;
}) => {
  let result;
  try {
    result = (
      await axiosInstance.post(
        `${REACT_APP_API_URL!}/v1/cabals/updateFreeModeCabals/${tgGroupId}`,
        body,
      )
    ).data;

    if (result.status < 300) {
      return {
        status: result.status,
        res: {
          tgGroupId,
        },
      };
    } else {
      return {
        status: result.status,
        message: result.message,
      };
    }
  } catch (error) {
    console.error("Failed to activate cabal by cabal id:", error);
    return {
      status: result.status,
      message: result.message,
    };
  }
};

const getJettonCabalCombined = async ({
  limit,
  page,
  filter,
  selects,
}: {
  limit: number;
  page: number;
  filter: Record<string, string | undefined>;
  selects: string[];
}) => {
  const queryString = qs.stringify({
    ...filter,
    limit,
    page,
    selects,
  });
  const endpoint = REACT_APP_API_URL + `/v1/cabals?${queryString}`;
  return (await axiosInstance.get<PaginatedData<Cabal | Jetton>>(endpoint)).data;
};

const getTelegramGroups = async ({
  limit,
  page,
  filter,
  selects,
}: {
  limit: number;
  page: number;
  filter: Record<string, string | undefined>;
  selects: string[];
}) => {
  const queryString = qs.stringify({
    ...filter,
    limit,
    page,
    selects,
  });
  const endpoint = REACT_APP_API_URL + `/v1/telegramGroup?${queryString}`;
  return (await axiosInstance.get(endpoint)).data;
};

const getTelegramGroupById = async ({ id }: { id: string }) => {
  const endpoint = REACT_APP_API_URL + `/v1/telegramGroup/${id}`;
  return (await axiosInstance.get(endpoint)).data;
};

const updateUserSlippage = async ({ slippage }: { slippage: number }) => {
  try {
    return (await axiosInstance.post(`${REACT_APP_API_URL!}/v1/user/slippage`, { slippage })).data;
  } catch (error) {
    console.error("Failed to update user slippage:", error);
  }
};

const updateUserPreferences = async ({
  slippage,
  validatorTip,
  priorityFee,
}: {
  slippage: number;
  validatorTip: number;
  priorityFee: number;
}) => {
  try {
    return (
      await axiosInstance.post(`${REACT_APP_API_URL!}/v1/user/preferences`, {
        preferences: {
          slippage,
          validatorTip,
          priorityFee,
        },
      })
    ).data;
  } catch (error) {
    console.error("Failed to update user preferences:", error);
  }
};

/* referral */
const getReferralCode = async ({
  limit,
  page,
  filter,
}: {
  limit: number;
  page: number;
  selects: string[];
  filter: Record<string, string | undefined>;
}) => {
  const queryString = qs.stringify({
    ...filter,
    limit,
    page,
  });

  return (await axiosInstance.get(`${REACT_APP_API_URL!}/v1/referral?${queryString}`)).data;
};

const getReferralOverview = async () => {
  return (await axiosInstance.get(`${REACT_APP_API_URL!}/v1/referral/overview`)).data;
};

const getReferrerByReferrerId = async ({ referrerId }: { referrerId: string }) => {
  return (await axiosInstance.get(`${REACT_APP_API_URL!}/v1/referral/referrer/${referrerId}`)).data;
};

const getProjectList = async ({ projectId }: { projectId: string }) => {
  const queryString = qs.stringify({
    projectId,
  });
  return (await axiosInstance.get(`${REACT_APP_API_URL!}/v1/projects?${queryString}`)).data;
};

const getShillData = async ({ projectId }: { projectId: string }) => {
  return (await axiosInstance.get(`${REACT_APP_API_URL!}/v1/projects/shill/${projectId}`)).data;
};

const updateProjectConversion = async (
  messageId: string,
  tgUserId: string | null,
  tgUserName: string,
  groupId: string,
  hitTime: Date,
) => {
  const result = await axiosInstance.post(`${REACT_APP_API_URL!}/v1/projectConversions/hit-count`, {
    messageId,
    tgUserName,
    tgUserId,
    groupId,
    hitTime,
  });

  return result;
};

const getReferralShillsOverviewByUserId = async ({ userId }: { userId: number }) => {
  const result = await axiosInstance.get(`${REACT_APP_API_URL!}/v1/referralShills/${userId}`);
  console.log(result);
  return result.data;
};

// const insertMockData = async ({ userId }: { userId: number }) => {
//   const result = await axiosInstance.post(`${REACT_APP_API_URL!}/v1/referralShills/mock-insert/${userId}`);
//   return result.data;
// };

// const removeMockData = async ({ userId }: { userId: number }) => {
//   const result = await axiosInstance.post(`${REACT_APP_API_URL!}/v1/referralShills/mock-remove/${userId}`);
//   return result.data;
// };

const axiosService = {
  getReferralShillsOverviewByUserId,
  updateProjectConversion,
  getTradeAllTrendingTokens,
  getTradeJoinedGroupTokens,
  getTradeToken,
  getNativePrice,
  getJettonUpdates,
  getJettonPrice,
  getJettonList,
  getTonPrice,
  getJetton,
  insertJetton,
  insertJettonV2,
  getJettonsByOwner,
  getJettonHolders,
  getJettonTxns,
  loginUser,
  createUser,
  joinGroup,
  updateJettonPurge,
  redeemCode,
  verifyToken,
  uploadFile,
  getGroupChatMsgs,
  getCabalCalls,
  getJoinedCabalCalls,
  getCabalDetails,
  getJettons,
  getJettonPriceV1,
  getJettonBalance,
  buyTonPreview,
  buyTonJetton,
  sellTonPreview,
  sellTonJetton,
  getTokenHolders,
  getToken,
  getCabalCallsPrice,
  getTokenBalance,
  tokenTradePreview,
  tokenTrade,
  crossChainTrade,
  crossChainTradeCustodial,
  traceEvent,
  joinGroupV1,
  getUserPortfolioAssets,
  getUserPortfolioGroups,
  getUserPortfolioEarning,
  getUserBalance,
  approveAllowance,
  getPrivateKey,
  getCurrentAllowance,
  createWallet,
  claimReward,
  createSolTokenAccount,
  txnUpdate,
  getJoinedCabal,
  buyJettonFromBridge,
  traceJobStatus,
  unwrapEth,
  unwrapSol,
  sendToken,
  sendNativeToken,
  getUser,
  updateUser,
  updateTrade,
  getTxnMessage,
  sendEmailAuth,
  getTonTxnMessage,
  sendLogs,
  activateCabalByCabalId,
  getAllCabals,
  getJettonCabalCombined,
  updateUserSlippage,
  getReferralCode,
  getReferralOverview,
  getReferrerByReferrerId,
  getTelegramGroups,
  getTelegramGroupById,
  updateUserPreferences,
  getProjectList,
  getShillData,
  // insertMockData,
  // removeMockData,
};

export default axiosService;
